import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConsumerLayout from './ConsumerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { delivery, orders as ordersApi } from '../../utils/api';
import { toast } from 'sonner';
import { 
  Truck, MapPin, Phone, Clock, RefreshCw, XCircle, CheckCircle2, 
  ArrowLeft, Package, Navigation, User, Store, Home, ChefHat, 
  ClipboardCheck, Send, UserCheck
} from 'lucide-react';

/**
 * Complete delivery tracking flow with verbose descriptions.
 * Combines ORDER statuses (before dispatch) with DOORDASH statuses (after dispatch).
 * 
 * The flow has two phases:
 * 1. ORDER PREPARATION: Managed by DealShaq/Retailer
 * 2. DOORDASH DELIVERY: Managed by DoorDash after dispatch
 */

// Phase 1: Order Preparation Steps (Before DoorDash Dispatch)
const ORDER_PREP_STEPS = [
  { 
    key: 'confirmed', 
    label: 'Order Confirmed', 
    icon: ClipboardCheck,
    description: 'The retailer has received and confirmed your order. They will begin preparing it shortly.',
    phase: 'preparation'
  },
  { 
    key: 'preparing', 
    label: 'Being Prepared', 
    icon: ChefHat,
    description: 'The retailer is now preparing your items. This typically takes 15-30 minutes depending on the order size.',
    phase: 'preparation'
  },
  { 
    key: 'ready', 
    label: 'Ready for Pickup', 
    icon: Package,
    description: 'Great news! Your order is ready and packed. The retailer is now requesting a DoorDash driver.',
    phase: 'preparation'
  },
];

// Phase 2: DoorDash Delivery Steps (After Dispatch)
const DOORDASH_STEPS = [
  { 
    key: 'created', 
    label: 'Delivery Requested', 
    icon: Send,
    description: 'A delivery request has been sent to DoorDash. They are now looking for an available Dasher in the area.',
    phase: 'delivery'
  },
  { 
    key: 'confirmed', 
    label: 'Dasher Assigned', 
    icon: UserCheck,
    description: 'A DoorDash driver (Dasher) has accepted your delivery. They will head to the store shortly.',
    phase: 'delivery',
    doordashKey: 'confirmed'
  },
  { 
    key: 'enroute_to_pickup', 
    label: 'Dasher En Route to Store', 
    icon: Truck,
    description: 'Your Dasher is on their way to the store to pick up your order. You can see their details below.',
    phase: 'delivery'
  },
  { 
    key: 'arrived_at_pickup', 
    label: 'Dasher Arrived at Store', 
    icon: Store,
    description: 'Your Dasher has arrived at the store and is collecting your order from the retailer.',
    phase: 'delivery'
  },
  { 
    key: 'picked_up', 
    label: 'Order Picked Up', 
    icon: Package,
    description: 'Your Dasher has picked up your order and is heading to their vehicle. Delivery is next!',
    phase: 'delivery'
  },
  { 
    key: 'enroute_to_dropoff', 
    label: 'On The Way to You', 
    icon: Navigation,
    description: 'Your order is on the move! The Dasher is driving to your delivery address right now.',
    phase: 'delivery'
  },
  { 
    key: 'arrived_at_dropoff', 
    label: 'Dasher Has Arrived', 
    icon: Home,
    description: 'Your Dasher has arrived at your delivery address. Please be ready to receive your order!',
    phase: 'delivery'
  },
  { 
    key: 'delivered', 
    label: 'Delivered!', 
    icon: CheckCircle2,
    description: 'Your order has been successfully delivered. Enjoy your items! Thank you for using DealShaq.',
    phase: 'delivery'
  },
];

// Combine all steps for the full journey
const ALL_STEPS = [...ORDER_PREP_STEPS, ...DOORDASH_STEPS];

export default function DeliveryTrackingPage({ user, onLogout }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  const [orderDetails, setOrderDetails] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);

  // Fetch order and delivery status
  const fetchData = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    
    try {
      // Get order details (includes order status like confirmed, preparing, ready)
      const ordersResponse = await ordersApi.list();
      const order = ordersResponse.data.find(o => o.id === orderId);
      setOrderDetails(order);
      
      // Get delivery info
      const deliveryResponse = await delivery.getOrderDelivery(orderId);
      setDeliveryInfo(deliveryResponse.data);
      
      // Get DoorDash delivery status if dispatched
      if (deliveryResponse.data.delivery_external_id) {
        const statusResponse = await delivery.getStatus(deliveryResponse.data.delivery_external_id);
        setDeliveryStatus(statusResponse.data);
        
        // Calculate countdown if we have estimated dropoff time
        if (statusResponse.data.dropoff_time_estimated) {
          const eta = new Date(statusResponse.data.dropoff_time_estimated);
          const now = new Date();
          const diff = Math.max(0, Math.floor((eta - now) / 1000 / 60));
          setCountdown(diff);
        }
      }
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load delivery information');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchData();
    
    // Poll every 20 seconds for updates
    const interval = setInterval(() => fetchData(false), 20000);
    
    // Countdown timer (updates every minute)
    const countdownInterval = setInterval(() => {
      setCountdown(prev => prev !== null && prev > 0 ? prev - 1 : prev);
    }, 60000);
    
    return () => {
      clearInterval(interval);
      clearInterval(countdownInterval);
    };
  }, [fetchData]);

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this delivery?')) return;
    
    try {
      await delivery.cancel(deliveryInfo.delivery_external_id);
      toast.success('Delivery cancelled successfully');
      fetchData(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel delivery');
    }
  };

  /**
   * Determine the current step index based on order status and DoorDash status.
   * 
   * Logic:
   * - If no DoorDash dispatch yet, use order status (confirmed, preparing, ready)
   * - If DoorDash dispatched, use DoorDash status
   */
  const getCurrentStepIndex = () => {
    const orderStatus = orderDetails?.status || 'confirmed';
    const doordashStatus = deliveryStatus?.delivery_status;
    
    // If cancelled, return special index
    if (doordashStatus === 'cancelled' || orderStatus === 'cancelled') {
      return -1;
    }
    
    // If DoorDash is dispatched, find the DoorDash step
    if (deliveryInfo?.delivery_external_id && doordashStatus) {
      // Find index in combined array - order prep steps + doordash step
      const doordashStepIndex = DOORDASH_STEPS.findIndex(s => s.key === doordashStatus);
      if (doordashStepIndex >= 0) {
        return ORDER_PREP_STEPS.length + doordashStepIndex;
      }
    }
    
    // Otherwise, use order status for preparation phase
    const orderStepIndex = ORDER_PREP_STEPS.findIndex(s => s.key === orderStatus);
    return orderStepIndex >= 0 ? orderStepIndex : 0;
  };

  const currentStepIndex = getCurrentStepIndex();
  const totalSteps = ALL_STEPS.length;
  const progressPercent = currentStepIndex === -1 ? 0 : 
    Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  // Get current step info for header
  const getCurrentStepInfo = () => {
    if (currentStepIndex === -1) {
      return { label: 'Cancelled', description: 'This delivery has been cancelled.' };
    }
    return ALL_STEPS[currentStepIndex] || ALL_STEPS[0];
  };
  const currentStep = getCurrentStepInfo();

  if (loading) {
    return (
      <ConsumerLayout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <RefreshCw className="w-10 h-10 animate-spin text-purple-500" />
          <p className="text-gray-600">Loading delivery tracking...</p>
        </div>
      </ConsumerLayout>
    );
  }

  if (error) {
    return (
      <ConsumerLayout user={user} onLogout={onLogout}>
        <div className="max-w-2xl mx-auto text-center py-12">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Tracking</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/consumer/orders')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </Button>
        </div>
      </ConsumerLayout>
    );
  }

  // If order doesn't have delivery method as "delivery"
  if (deliveryInfo?.delivery_method !== 'delivery') {
    return (
      <ConsumerLayout user={user} onLogout={onLogout}>
        <div className="max-w-2xl mx-auto text-center py-12">
          <MapPin className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">This is a Pickup Order</h2>
          <p className="text-gray-600 mb-6">
            Delivery tracking is only available for delivery orders. 
            This order is scheduled for pickup.
          </p>
          <Button onClick={() => navigate('/consumer/orders')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </Button>
        </div>
      </ConsumerLayout>
    );
  }

  const isCancelled = currentStepIndex === -1;
  const isDelivered = deliveryStatus?.delivery_status === 'delivered';
  const isDispatched = !!deliveryInfo?.delivery_external_id;
  
  // Can cancel only before pickup
  const canCancel = !isCancelled && !isDelivered && isDispatched &&
    !['picked_up', 'enroute_to_dropoff', 'arrived_at_dropoff', 'delivered'].includes(deliveryStatus?.delivery_status);

  return (
    <ConsumerLayout user={user} onLogout={onLogout}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/consumer/orders')} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Orders
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Main Tracking Card */}
        <Card className="overflow-hidden shadow-lg">
          {/* Status Header */}
          <div className={`p-6 text-white ${
            isCancelled ? 'bg-red-500' : 
            isDelivered ? 'bg-green-500' : 
            'bg-gradient-to-r from-purple-600 to-purple-700'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm opacity-90 mb-1">Order #{orderId?.slice(0, 8)}</p>
                <h1 className="text-2xl font-bold">
                  {currentStep.label}
                </h1>
                <p className="text-sm opacity-90 mt-2 max-w-md">
                  {currentStep.description}
                </p>
              </div>
              
              {/* ETA Countdown */}
              {!isCancelled && !isDelivered && countdown !== null && isDispatched && (
                <div className="text-right ml-4">
                  <p className="text-sm opacity-90">Estimated Arrival</p>
                  <p className="text-4xl font-bold">{countdown}</p>
                  <p className="text-sm opacity-90">minutes</p>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {!isCancelled && (
              <div className="mt-6">
                <div className="flex justify-between text-xs mb-2 opacity-90">
                  <span>Order Placed</span>
                  <span>Delivered</span>
                </div>
                <div className="h-3 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="text-sm mt-2 opacity-90 text-center">
                  Step {currentStepIndex + 1} of {totalSteps} • {progressPercent}% Complete
                </p>
              </div>
            )}
          </div>

          <CardContent className="p-6 space-y-6">
            
            {/* Phase Separator: Order Preparation */}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Order Preparation
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* Order Preparation Steps */}
            <div className="space-y-0">
              {ORDER_PREP_STEPS.map((step, index) => {
                const isComplete = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const StepIcon = step.icon;
                
                return (
                  <div key={step.key} className="flex items-start gap-4">
                    {/* Icon & Connector Line */}
                    <div className="flex flex-col items-center">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all
                        ${isComplete 
                          ? 'bg-emerald-500 border-emerald-500 text-white' 
                          : 'bg-white border-gray-200 text-gray-400'}
                        ${isCurrent ? 'ring-4 ring-emerald-100 scale-110' : ''}
                      `}>
                        {isComplete && !isCurrent ? (
                          <CheckCircle2 className="w-6 h-6" />
                        ) : (
                          <StepIcon className="w-6 h-6" />
                        )}
                      </div>
                      {index < ORDER_PREP_STEPS.length - 1 && (
                        <div className={`w-0.5 h-12 ${isComplete ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                      )}
                    </div>
                    
                    {/* Step Content */}
                    <div className={`flex-1 pb-6 ${isCurrent ? '' : ''}`}>
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${
                          isCurrent ? 'text-emerald-600 text-lg' : 
                          isComplete ? 'text-gray-900' : 'text-gray-400'
                        }`}>
                          {step.label}
                        </h3>
                        {isCurrent && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm mt-1 ${
                        isCurrent ? 'text-gray-700' : 
                        isComplete ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Phase Separator: DoorDash Delivery */}
            <div className="flex items-center gap-3 pt-4">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                DoorDash Delivery
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* Not Yet Dispatched Message */}
            {!isDispatched && !isCancelled && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Waiting for Dispatch</p>
                  <p className="text-sm text-amber-700 mt-1">
                    The delivery will be dispatched to DoorDash once the retailer marks your order as "Ready for Pickup". 
                    You'll see real-time Dasher tracking here once that happens.
                  </p>
                </div>
              </div>
            )}

            {/* DoorDash Delivery Steps */}
            {(isDispatched || isCancelled) && (
              <div className="space-y-0">
                {DOORDASH_STEPS.map((step, index) => {
                  const absoluteIndex = ORDER_PREP_STEPS.length + index;
                  const isComplete = absoluteIndex <= currentStepIndex;
                  const isCurrent = absoluteIndex === currentStepIndex;
                  const StepIcon = step.icon;
                  
                  return (
                    <div key={step.key} className="flex items-start gap-4">
                      {/* Icon & Connector Line */}
                      <div className="flex flex-col items-center">
                        <div className={`
                          w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all
                          ${isComplete 
                            ? 'bg-purple-600 border-purple-600 text-white' 
                            : 'bg-white border-gray-200 text-gray-400'}
                          ${isCurrent ? 'ring-4 ring-purple-100 scale-110' : ''}
                        `}>
                          {isComplete && !isCurrent ? (
                            <CheckCircle2 className="w-6 h-6" />
                          ) : (
                            <StepIcon className="w-6 h-6" />
                          )}
                        </div>
                        {index < DOORDASH_STEPS.length - 1 && (
                          <div className={`w-0.5 h-12 ${isComplete ? 'bg-purple-600' : 'bg-gray-200'}`} />
                        )}
                      </div>
                      
                      {/* Step Content */}
                      <div className={`flex-1 pb-6`}>
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold ${
                            isCurrent ? 'text-purple-600 text-lg' : 
                            isComplete ? 'text-gray-900' : 'text-gray-400'
                          }`}>
                            {step.label}
                          </h3>
                          {isCurrent && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                              Current
                            </Badge>
                          )}
                        </div>
                        <p className={`text-sm mt-1 ${
                          isCurrent ? 'text-gray-700' : 
                          isComplete ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Cancelled State */}
            {isCancelled && (
              <div className="text-center py-8 bg-red-50 rounded-xl">
                <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-800">Delivery Cancelled</h3>
                <p className="text-red-600 mt-2">This delivery has been cancelled and is no longer active.</p>
              </div>
            )}

            {/* Dasher Info Card */}
            {deliveryStatus?.dasher_name && !isCancelled && (
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200">
                <p className="text-xs text-purple-600 uppercase tracking-wide font-semibold mb-3">
                  Your DoorDash Driver
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-purple-200 rounded-full flex items-center justify-center">
                      <User className="w-7 h-7 text-purple-700" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">{deliveryStatus.dasher_name}</p>
                      <p className="text-sm text-purple-600">DoorDash Dasher</p>
                    </div>
                  </div>
                  {deliveryStatus.dasher_phone_number && (
                    <a 
                      href={`tel:${deliveryStatus.dasher_phone_number}`}
                      className="flex items-center gap-2 bg-purple-600 text-white px-5 py-3 rounded-xl hover:bg-purple-700 transition-colors font-medium"
                    >
                      <Phone className="w-5 h-5" />
                      Call Dasher
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Delivery Address */}
            <div className="bg-gray-50 rounded-xl p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">
                Delivery Address
              </p>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-purple-600 mt-0.5" />
                <p className="text-gray-900 font-medium">{deliveryInfo?.delivery_address || 'Address not available'}</p>
              </div>
            </div>

            {/* Estimated & Actual Times */}
            {(deliveryStatus?.pickup_time_estimated || deliveryStatus?.dropoff_time_estimated) && !isCancelled && (
              <div className="grid grid-cols-2 gap-4">
                {deliveryStatus.pickup_time_estimated && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">
                      Store Pickup
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {new Date(deliveryStatus.pickup_time_estimated).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    {deliveryStatus.pickup_time_actual && (
                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Picked up at {new Date(deliveryStatus.pickup_time_actual).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                )}
                {deliveryStatus.dropoff_time_estimated && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">
                      Your Delivery
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {new Date(deliveryStatus.dropoff_time_estimated).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    {deliveryStatus.dropoff_time_actual && (
                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Delivered at {new Date(deliveryStatus.dropoff_time_actual).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Delivery ID & Mock Badge */}
            <div className="text-center text-xs text-gray-400 pt-4 border-t border-gray-100">
              <span>Delivery ID: {deliveryInfo?.delivery_external_id || 'Pending'}</span>
              {deliveryStatus?.is_mock && (
                <Badge variant="outline" className="ml-2 text-xs">Sandbox Mode</Badge>
              )}
            </div>

            {/* Cancel Button */}
            {canCancel && (
              <Button 
                variant="outline" 
                className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                onClick={handleCancel}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel Delivery
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </ConsumerLayout>
  );
}
